// src/components/Footer.jsx - Advanced Modern Footer with Updated Info
import { NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import ThemeToggle from './ThemeToggle';
import {
  FiTwitter,
  FiLinkedin,
  FiDribbble,
  FiGithub,
  FiBookOpen,
  FiArrowRight,
  FiHeart,
  FiZap,
  FiGlobe,
  FiMail,
  FiMapPin,
  FiClock,
  FiChevronRight,
  FiFacebook,
  FiInstagram,
} from 'react-icons/fi';
import { TfiPinterest } from 'react-icons/tfi';
import { FaBehance } from 'react-icons/fa';

// =============================================
// ANIMATED SOCIAL ICON - Smaller size
// =============================================
const AnimatedSocialIcon = ({ icon: Icon, href, label, color }) => {
  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="relative group"
      aria-label={label}
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.95 }}
    >
      <motion.div
        className="relative p-2 rounded-lg transition-all duration-300 overflow-hidden"
        style={{
          backgroundColor: 'var(--bg)',
          border: '1px solid var(--border)',
        }}
        whileHover={{
          borderColor: color,
          boxShadow: `0 4px 20px ${color}20`,
        }}
      >
        {/* Hover gradient overlay */}
        <motion.div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{
            background: `linear-gradient(135deg, ${color}10, transparent 70%)`,
          }}
        />

        <Icon
          className="w-4 h-4 relative z-10 transition-all duration-300 group-hover:scale-110"
          style={{ color: 'var(--text)' }}
        />

        {/* Animated dot indicator */}
        <motion.div
          className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full"
          style={{ backgroundColor: color }}
          initial={{ scale: 0, opacity: 0 }}
          whileHover={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 300 }}
        />
      </motion.div>
    </motion.a>
  );
};

// =============================================
// ANIMATED NAV LINK
// =============================================
const AnimatedNavLink = ({ to, children }) => {
  return (
    <motion.li whileHover={{ x: 4 }} className="relative group">
      <NavLink
        to={to}
        className={({ isActive }) =>
          `flex items-center gap-2 text-sm transition-all duration-300 ${
            isActive ? 'font-medium' : ''
          }`
        }
        style={({ isActive }) => ({
          color: isActive ? 'var(--text)' : 'var(--muted)',
        })}
      >
        <FiChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-0 -translate-x-2 flex-shrink-0" />
        <span>{children}</span>
      </NavLink>
    </motion.li>
  );
};

// =============================================
// FOOTER SECTION WITH ANIMATION
// =============================================
const FooterSection = ({ title, children, delay = 0 }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5, ease: 'easeOut' }}
      viewport={{ once: true }}
      className="group"
    >
      <h3 className="text-sm font-semibold uppercase tracking-wider mb-4 flex items-center gap-2">
        <span
          className="w-1 h-4 rounded-full flex-shrink-0"
          style={{ backgroundColor: 'var(--text)' }}
        />
        {title}
      </h3>
      {children}
    </motion.div>
  );
};

function Footer() {
  const currentYear = new Date().getFullYear();

  // Updated social links with correct URLs
  const socialLinks = [
    {
      name: 'Facebook',
      icon: FiFacebook,
      href: 'https://www.facebook.com/wdhejbul',
      color: '#1877F2',
    },
    {
      name: 'Instagram',
      icon: FiInstagram,
      href: 'https://www.instagram.com/wdhejbul/',
      color: '#E4405F',
    },
    {
      name: 'LinkedIn',
      icon: FiLinkedin,
      href: 'https://www.linkedin.com/in/hejbul-rahman-tasin-127350265',
      color: '#0A66C2',
    },
    {
      name: 'Twitter',
      icon: FiTwitter,
      href: 'https://x.com/wdhejbul',
      color: '#1DA1F2',
    },
    {
      name: 'Pinterest',
      icon: TfiPinterest,
      href: 'https://www.pinterest.com/wdhejbul',
      color: '#E60023',
    },
    {
      name: 'Behance',
      icon: FaBehance,
      href: 'https://www.behance.net/wdhejbul',
      color: '#1769FF',
    },
    {
      name: 'GitHub',
      icon: FiGithub,
      href: 'https://github.com/wdhejbul',
      color: '#6e5494',
    },
  ];

  // Microstock links for bottom bar
  const microstockLinks = [
    {
      name: 'AdobeStock',
      href: 'https://stock.adobe.com/contributor/211482367/wdhejbul',
    },
    { name: 'ShutterStock', href: 'https://www.shutterstock.com/g/wdhejbul' },
    { name: 'Vecteezy', href: 'https://www.vecteezy.com/members/wdhejbul' },
  ];

  const navLinks = [
    { name: 'Portfolio', path: '/portfolio' },
    { name: 'Services', path: '/services' },
    { name: 'Subscription', path: '/subscription' },
    { name: 'Blog', path: '/blog' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const quickInfo = [
    { icon: FiMail, label: 'Email', value: 'tasinahamed503@gmail.com' },
    { icon: FiMapPin, label: 'Location', value: 'Dhaka, Bangladesh' },
    { icon: FiGlobe, label: 'Available', value: 'Worldwide Remote' },
  ];

  return (
    <footer
      className="relative mt-20 overflow-hidden w-full"
      style={{
        backgroundColor: 'var(--bg)',
        borderTop: '1px solid var(--border)',
      }}
    >
      {/* ============================================ */}
      {/* ANIMATED GRADIENT BACKGROUND */}
      {/* ============================================ */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            x: ['-10%', '10%', '-10%'],
            y: ['-10%', '15%', '-10%'],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          className="absolute -top-40 -right-40 w-96 h-96 rounded-full blur-3xl"
          style={{
            background: 'radial-gradient(circle, #3b82f6 0%, transparent 70%)',
            opacity: 0.04,
          }}
        />
        <motion.div
          animate={{
            x: ['15%', '-10%', '15%'],
            y: ['15%', '-10%', '15%'],
            scale: [1.2, 1, 1.2],
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full blur-3xl"
          style={{
            background: 'radial-gradient(circle, #8b5cf6 0%, transparent 70%)',
            opacity: 0.03,
          }}
        />
        <motion.div
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-[120px]"
          style={{
            background:
              'radial-gradient(circle, var(--border) 0%, transparent 70%)',
            opacity: 0.04,
          }}
        />

        {/* Animated shimmer line */}
        <motion.div
          animate={{
            x: ['-100%', '100%'],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          className="absolute top-0 left-0 right-0 h-px w-1/3"
          style={{
            background:
              'linear-gradient(to right, transparent, var(--text), transparent)',
            opacity: 0.06,
          }}
        />
      </div>

      {/* ============================================ */}
      {/* MAIN FOOTER CONTENT - Fixed Layout */}
      {/* ============================================ */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20 w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-8 md:gap-12 w-full">
          {/* Brand Section - 4 columns */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="lg:col-span-4 sm:col-span-2"
          >
            <motion.div whileHover={{ scale: 1.02 }} className="inline-block">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
                <span style={{ color: 'var(--text)' }}>WD</span>
                <span style={{ color: 'var(--muted)' }}>Hajbul</span>
                <motion.span
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                >
                  <FiHeart className="w-4 h-4" style={{ color: '#ef4444' }} />
                </motion.span>
              </h2>
            </motion.div>

            <p
              className="text-sm leading-relaxed mt-3 max-w-sm"
              style={{ color: 'var(--muted)' }}
            >
              I design clean, impactful visual experiences for brands. Focused
              on product design, branding, and modern digital interfaces.
            </p>

            {/* Quick Info - Updated */}
            <div className="mt-6 space-y-2.5">
              {quickInfo.map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  viewport={{ once: true }}
                  className="flex items-center gap-3 text-sm"
                  style={{ color: 'var(--muted)' }}
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  <span>
                    <span className="opacity-60">{item.label}:</span>{' '}
                    <span style={{ color: 'var(--text)' }}>{item.value}</span>
                  </span>
                </motion.div>
              ))}
            </div>

            {/* Social Icons - Updated with all platforms */}
            <div className="mt-6 flex gap-2 flex-wrap">
              {socialLinks.map((social, idx) => (
                <AnimatedSocialIcon
                  key={idx}
                  icon={social.icon}
                  href={social.href}
                  label={social.name}
                  color={social.color}
                />
              ))}
            </div>
          </motion.div>

          {/* Navigation - 2 columns */}
          <div className="lg:col-span-2 sm:col-span-1">
            <FooterSection title="Navigation" delay={0.1}>
              <ul className="space-y-2.5">
                {navLinks.slice(0, 3).map((item, idx) => (
                  <AnimatedNavLink key={idx} to={item.path}>
                    {item.name}
                  </AnimatedNavLink>
                ))}
              </ul>
            </FooterSection>
          </div>

          {/* Quick Links - 2 columns */}
          <div className="lg:col-span-2 sm:col-span-1">
            <FooterSection title="Quick Links" delay={0.2}>
              <ul className="space-y-2.5">
                {navLinks.slice(3).map((item, idx) => (
                  <AnimatedNavLink key={idx} to={item.path}>
                    {item.name}
                  </AnimatedNavLink>
                ))}
                <motion.li whileHover={{ x: 4 }} className="relative group">
                  <NavLink
                    to="/privacy-policy"
                    className="flex items-center gap-2 text-sm transition-all duration-300"
                    style={{ color: 'var(--muted)' }}
                  >
                    <FiChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-0 -translate-x-2 flex-shrink-0" />
                    <span>Privacy Policy</span>
                  </NavLink>
                </motion.li>
                <motion.li whileHover={{ x: 4 }} className="relative group">
                  <NavLink
                    to="/terms"
                    className="flex items-center gap-2 text-sm transition-all duration-300"
                    style={{ color: 'var(--muted)' }}
                  >
                    <FiChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-0 -translate-x-2 flex-shrink-0" />
                    <span>Terms & Conditions</span>
                  </NavLink>
                </motion.li>
              </ul>
            </FooterSection>
          </div>

          {/* CTA Section - 4 columns */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            viewport={{ once: true }}
            className="lg:col-span-4 sm:col-span-2"
          >
            <div
              className="relative p-6 rounded-2xl overflow-hidden h-full"
              style={{
                backgroundColor: 'var(--border)',
                border: '1px solid var(--border)',
              }}
            >
              {/* Subtle glow */}
              <div className="absolute inset-0 opacity-20">
                <div
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full blur-2xl"
                  style={{
                    background:
                      'radial-gradient(circle, var(--text) 0%, transparent 70%)',
                  }}
                />
              </div>

              <div className="relative z-10">
                <h3
                  className="text-lg font-bold flex items-center gap-2"
                  style={{ color: 'var(--text)' }}
                >
                  <FiZap className="w-5 h-5" style={{ color: '#f59e0b' }} />
                  Let's Work Together
                </h3>
                <p
                  className="text-sm mt-2 mb-4"
                  style={{ color: 'var(--muted)' }}
                >
                  Have a project in mind? Let's build something meaningful and
                  modern.
                </p>
                <motion.div
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="inline-block"
                >
                  <NavLink
                    to="/hire"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 group"
                    style={{
                      backgroundColor: 'var(--text)',
                      color: 'var(--bg)',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
                    }}
                  >
                    <span>Hire Me</span>
                    <FiArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </NavLink>
                </motion.div>

                {/* Theme Toggle */}
                <div
                  className="mt-4 pt-4 border-t"
                  style={{ borderColor: 'var(--border)' }}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: 'var(--muted)' }}>
                      Theme
                    </span>
                    <ThemeToggle />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ============================================ */}
      {/* BOTTOM BAR - Updated with Microstock Links */}
      {/* ============================================ */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.5 }}
        viewport={{ once: true }}
        style={{
          borderTop: '1px solid var(--border)',
          position: 'relative',
        }}
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{
              x: ['-100%', '100%'],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            className="absolute top-0 left-0 right-0 h-px w-1/2"
            style={{
              background:
                'linear-gradient(to right, transparent, var(--text), transparent)',
              opacity: 0.04,
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 py-5 md:py-6 w-full">
          <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-4 text-sm w-full">
            <motion.div
              className="flex flex-col sm:flex-row items-center gap-3 sm:gap-5"
              whileHover={{ scale: 1.01 }}
            >
              <p style={{ color: 'var(--muted)' }}>
                © {currentYear} WD Hajbul. All rights reserved.
              </p>
              <div
                className="hidden sm:block w-px h-4 flex-shrink-0"
                style={{ backgroundColor: 'var(--border)' }}
              />
              <motion.div
                className="flex items-center gap-1.5 text-xs"
                style={{ color: 'var(--muted)' }}
                whileHover={{ color: 'var(--text)' }}
              >
                <FiHeart className="w-3 h-3 text-red-400" />
                <span>Made with passion</span>
              </motion.div>
            </motion.div>

            {/* Microstock Links - Replaced social text links */}
            <div className="flex items-center gap-4">
              {microstockLinks.map((link, idx) => (
                <motion.a
                  key={idx}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs transition-all duration-300"
                  style={{ color: 'var(--muted)' }}
                  whileHover={{ color: 'var(--text)', scale: 1.05 }}
                >
                  {link.name}
                </motion.a>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </footer>
  );
}

export default Footer;
